/**
 * Admin · Testimonials card — list of ratings/comments with admin curation.
 * Admins can Publish/Unpublish each testimonial to the public landing page
 * (publishing requires the customer's `allow_public` consent) and Delete.
 * Anonymity: when no name was given, the company is hidden here too.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Star, Eye, EyeOff, Trash2, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminTestimonialsCard({ testimonials, onChanged }) {
  const avg = testimonials.length > 0
    ? (testimonials.reduce((s, t) => s + (t.rating || 0), 0) / testimonials.length).toFixed(1)
    : null;

  return (
    <div data-testid="admin-testimonials-card" className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 text-amber-500" />
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Admin · Testimonials</p>
      </div>
      <div className="mt-2 flex items-baseline gap-3 flex-wrap">
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Ratings &amp; feedback</h3>
        {testimonials.length > 0 && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <b>{testimonials.length}</b> response{testimonials.length === 1 ? "" : "s"}
            {" · "}avg <b>{avg}</b>/5
          </p>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Published testimonials appear on the public landing page. You can only publish ones where the customer ticked &ldquo;use publicly&rdquo;.
      </p>

      {testimonials.length === 0 ? (
        <p className="mt-4 text-xs italic text-slate-500 dark:text-slate-400">
          No ratings yet. Each plan email includes a one-click feedback link — they'll appear here once sent.
        </p>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="admin-testimonials-list">
          {testimonials.map((t) => (
            <TestimonialRow key={t.id} t={t} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TestimonialRow({ t, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [placements, setPlacements] = useState(t.placements || []);
  const anonymous = t.is_anonymous || !(t.name || "").trim();

  const setPublished = async (next) => {
    setBusy(true);
    try {
      await api.put(`/admin/testimonials/${t.id}`, { published: next });
      toast.success(next ? "Published." : "Unpublished.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const togglePlacement = async (surface) => {
    const next = placements.includes(surface)
      ? placements.filter((p) => p !== surface)
      : [...placements, surface];
    setPlacements(next);
    try {
      await api.put(`/admin/testimonials/${t.id}`, { placements: next });
      toast.success("Placement updated.");
      onChanged?.();
    } catch (e) {
      setPlacements(placements); // revert
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Testimonial deleted.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900 p-4" data-testid={`testimonial-row-${t.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`h-3.5 w-3.5 ${n <= (t.rating || 0) ? "fill-amber-400 text-amber-500" : "text-slate-300 dark:text-slate-600"}`} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {t.published ? (
            <span className="px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-500/15 text-teal-800 dark:text-teal-300 text-[10px] font-medium uppercase tracking-wide">Published</span>
          ) : t.allow_public ? (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-medium uppercase tracking-wide">Consent OK</span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 text-[10px] font-medium uppercase tracking-wide">No consent</span>
          )}
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}
          </span>
        </div>
      </div>

      {t.comment && (
        <p className="mt-2 text-sm text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-wrap">
          &ldquo;{t.comment}&rdquo;
        </p>
      )}

      <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
        {anonymous ? "Anonymous" : t.name}
        {t.role ? <> · <span>{t.role}</span></> : null}
        {!anonymous && t.organization ? <> · <span className="text-slate-500 dark:text-slate-400">{t.organization}</span></> : null}
        {t.industry ? <> · <span className="text-slate-500 dark:text-slate-400">{t.industry}</span></> : null}
      </p>

      <div className="mt-3 flex items-center gap-2">
        {t.published ? (
          <button
            onClick={() => setPublished(false)}
            disabled={busy}
            data-testid={`testimonial-unpublish-${t.id}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />} Unpublish
          </button>
        ) : (
          <button
            onClick={() => setPublished(true)}
            disabled={busy || !t.allow_public}
            title={t.allow_public ? "" : "Customer did not consent to public use"}
            data-testid={`testimonial-publish-${t.id}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-500 disabled:opacity-40 transition-colors"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Publish
          </button>
        )}
        <button
          onClick={remove}
          disabled={busy}
          data-testid={`testimonial-delete-${t.id}`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-red-200 dark:border-red-500/40 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-50 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
      </div>

      {t.allow_public && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800">
          <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-2">Show on</p>
          <div className="flex flex-wrap gap-2">
            {[["homepage", "Homepage"], ["pricing", "Pricing"], ["testimonials", "/testimonials"]].map(([key, label]) => {
              const on = placements.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => togglePlacement(key)}
                  data-testid={`testimonial-placement-${key}-${t.id}`}
                  className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11px] font-medium border transition-colors ${
                    on
                      ? "border-teal-600 bg-teal-50 dark:bg-teal-500/15 text-teal-800 dark:text-teal-200"
                      : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  {on ? <Check className="h-3 w-3" /> : null} {label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">Adding a placement publishes it to that surface. Remove all to hide everywhere.</p>
        </div>
      )}
    </li>
  );
}
